import {
  createContext, useContext, useEffect, useState, type CSSProperties, type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Badge, Box, Burger, Button, Divider, Drawer, Group, Stack, Text, UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useAuth } from '../../auth/AuthContext.js';
import { clientSalesApi } from '../../api/clientSales.js';
import { NotificationsBell } from '../../pages/NotificationsBell.js';
import { NowBar } from '../NowBar.js';
import logoHorizontal from '../../assets/auth/logo-horizontal.svg';
import {
  HomeIcon, BagIcon, WalletIcon, ChartIcon, UserIcon, NewsIcon,
} from './clientIcons.js';

// Protected пересоздаёт ClientShell на каждом переходе между страницами, поэтому ответ
// meta() кэшируем на модуль — один запрос на сессию пользователя. Ключ по email: кэш
// переживает переходы между страницами, но НЕ смену аккаунта в той же вкладке (иначе
// видимость «Аналитики» протекала бы от прошлого клиента к следующему).
let hasSalesCache: { email: string; value: boolean } | null = null;

const ClientNavContext = createContext<{ hasSales: boolean }>({ hasSales: false });
export function useClientNav() { return useContext(ClientNavContext); }

const TABS: Array<{ to: string; label: string; icon: (p: { size?: number }) => ReactNode; needsSales?: boolean }> = [
  { to: '/', label: 'Главная', icon: (p) => <HomeIcon {...p} /> },
  { to: '/client/orders', label: 'Заказы', icon: (p) => <BagIcon {...p} /> },
  { to: '/client/finance', label: 'Финансы', icon: (p) => <WalletIcon {...p} /> },
  { to: '/client/sales', label: 'Аналитика', icon: (p) => <ChartIcon {...p} />, needsSales: true },
  { to: '/profile', label: 'Профиль', icon: (p) => <UserIcon {...p} /> },
];

const DRAWER_LINKS: Array<{ to: string; label: string; icon: ReactNode; needsSales?: boolean }> = [
  { to: '/news', label: 'Новости', icon: <NewsIcon size={20} /> },
  { to: '/client/orders', label: 'Мои заказы', icon: <BagIcon size={20} /> },
  { to: '/client/finance', label: 'Финансы', icon: <WalletIcon size={20} /> },
  { to: '/client/sales', label: 'Аналитика продаж', icon: <ChartIcon size={20} />, needsSales: true },
  { to: '/profile', label: 'Мой профиль', icon: <UserIcon size={20} /> },
];

const TABBAR_HEIGHT = 62;

function isActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname.startsWith(to);
}

export function ClientShell({ children }: { children: ReactNode }) {
  const { state, logout } = useAuth();
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery('(min-width: 62em)', false, { getInitialValueInEffect: false });
  const [menuOpen, setMenuOpen] = useState(false);
  // Хуки обязаны выполниться до early-return по статусу, поэтому email берём из state напрямую.
  const email = state.status === 'authed' ? state.user.email : null;
  const cached = hasSalesCache && hasSalesCache.email === email ? hasSalesCache.value : null;
  const [hasSales, setHasSales] = useState(cached ?? false);

  useEffect(() => {
    if (!email) return;
    // Кэш чужого аккаунта не подходит — перезапрашиваем и заодно сбрасываем показанное значение.
    if (hasSalesCache && hasSalesCache.email === email) {
      setHasSales(hasSalesCache.value);
      return;
    }
    setHasSales(false);
    clientSalesApi.meta()
      .then((m) => {
        const v = m.hasB2bL2 || m.hasRidaL2;
        hasSalesCache = { email, value: v };
        setHasSales(v);
      })
      .catch(() => { /* нет данных — пункт «Аналитика» просто не показываем */ });
  }, [email]);

  // Drawer закрываем при любом переходе (клик по ссылке внутри).
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  if (state.status !== 'authed') return null;
  const u = state.user;

  const tabs = TABS.filter((t) => !t.needsSales || hasSales);
  const drawerLinks = DRAWER_LINKS.filter((l) => !l.needsSales || hasSales);

  const headerStyle: CSSProperties = {
    background: '#fff',
    boxShadow: '0 1px 3px rgb(20 32 27 / 6%)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  };

  return (
    <ClientNavContext.Provider value={{ hasSales }}>
      <Box style={{ minHeight: '100vh', background: '#F5F6F8' }}>
        {/* Шапка */}
        <Box px={isDesktop ? 28 : 16} py={isDesktop ? 10 : 12} style={headerStyle}>
          {isDesktop ? (
            <Group justify="space-between" wrap="nowrap">
              <Group gap={20} wrap="nowrap">
                <Link to="/" style={{ display: 'flex' }} aria-label="На главную">
                  <img src={logoHorizontal} alt="EURASIA" style={{ width: 170, display: 'block' }} />
                </Link>
                <Text fz={15} c="#14201B">Корпоративный портал ООО ТК Евразия</Text>
              </Group>
              <Stack gap={2} align="flex-end">
                <NowBar />
                <Group gap="xs" wrap="nowrap">
                  <NotificationsBell />
                  <Text size="sm" c="#6B7570">{u.email}</Text>
                  <Button variant="subtle" color="#0C8C7A" size="compact-sm" onClick={logout}>Выйти</Button>
                </Group>
              </Stack>
            </Group>
          ) : (
            <Group justify="space-between" wrap="nowrap">
              <Link to="/" style={{ display: 'flex' }} aria-label="На главную">
                <img src={logoHorizontal} alt="EURASIA" style={{ width: 140, display: 'block' }} />
              </Link>
              <Group gap={4} wrap="nowrap">
                <NotificationsBell />
                <Burger opened={menuOpen} onClick={() => setMenuOpen((v) => !v)} aria-label="Меню" size="sm" />
              </Group>
            </Group>
          )}
        </Box>

        {/* Мобильное меню-бургер */}
        <Drawer opened={menuOpen} onClose={() => setMenuOpen(false)} position="right" size="80%" title="Меню">
          <Stack gap={4}>
            {drawerLinks.map((l) => (
              <UnstyledButton
                key={l.to}
                component={Link}
                to={l.to}
                p="sm"
                style={{
                  borderRadius: 10,
                  color: isActive(pathname, l.to) ? '#0C8C7A' : '#14201B',
                  background: isActive(pathname, l.to) ? '#E6F7F3' : undefined,
                }}
              >
                <Group gap="sm">{l.icon}<Text fw={500}>{l.label}</Text></Group>
              </UnstyledButton>
            ))}
            <Divider my="sm" />
            <Button variant="subtle" color="#0C8C7A" onClick={logout}>Выйти</Button>
          </Stack>
        </Drawer>

        {/* Контент страницы; на мобиле отступ снизу под таб-бар */}
        <Box style={!isDesktop ? { paddingBottom: `calc(${TABBAR_HEIGHT}px + env(safe-area-inset-bottom))` } : undefined}>
          {children}
        </Box>

        {/* Нижний таб-бар (только мобила) */}
        {!isDesktop && (
          <Box
            component="nav"
            style={{
              position: 'fixed',
              left: 0, right: 0, bottom: 0,
              background: '#fff',
              borderTop: '1px solid #E4E7E6',
              paddingBottom: 'env(safe-area-inset-bottom)',
              zIndex: 100,
            }}
          >
            <Group justify="space-around" gap={0} style={{ height: TABBAR_HEIGHT }}>
              {tabs.map((t) => {
                const active = isActive(pathname, t.to);
                return (
                  <UnstyledButton
                    key={t.to}
                    component={Link}
                    to={t.to}
                    style={{
                      flex: 1,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 3,
                      color: active ? '#0FAC97' : '#6B7570',
                    }}
                  >
                    {t.icon({ size: 22 })}
                    <Text fz={11} fw={active ? 600 : 400} lh={1}>{t.label}</Text>
                  </UnstyledButton>
                );
              })}
            </Group>
          </Box>
        )}
      </Box>
    </ClientNavContext.Provider>
  );
}
